import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { salesApi } from '../../../../config/apiClient';
import { PhoneCall, PackageCustomer, CustomerAddress } from '../types';

// ============================================================================
// PHONE CALLS HOOK - CallerID, SSE, Electron Entegrasyonu (Kuyruk Mimarisi)
// ============================================================================
//
// MİMARİ: Dual state — `activeCall` (yanıtlanan / işlem gören aktif arama) +
// `pendingCalls[]` (sırada bekleyen aramalar). Eski `incomingCall` ve
// `activePhoneCallId` API'leri türetilmiş selector olarak korunur, böylece
// dünkü Faz 1-3 (anyModalOpen hibrit, reject button, cross-call name leak)
// değişiklikleri kırılmaz.
//
// Bug fix: Eski "yeniyle değiştir" mantığı (usePhoneCalls eski 363-379 satırlar)
// kaldırıldı; yeni arama gelirse aktif arama silinmez, pending kuyruğa eklenir.
// ============================================================================

export interface QueuedCall {
  call: PhoneCall;
  dbCallId: number | null;
  startedAt: number;
  answeredAt: number | null;
  isAnswered: boolean;
  /**
   * Birleşik kuyruk: ekran meşgulken (müşteri bağlamı / modal / aktif arama)
   * gelen arama held olarak kuyruğa girer — zil kesilse bile kuyruktan
   * SİLİNMEZ, kasiyer karar verene veya sırası gelene kadar bekler.
   */
  isHeld: boolean;
  /** Zil sinyali kesildi (fiziksel çağrı büyük olasılıkla sonlandı) */
  ringEnded: boolean;
  /** DB kaydına CallEndTime yazıldı — aynı kayıt tekrar kapatılmaz */
  dbClosed: boolean;
}

export interface UsePhoneCallsOptions {
  companyId: string | null | undefined;
  screenId: string | undefined;
  branchId: string | null | undefined;
  packageScreenId: number | null | undefined;
  localServiceUrl: string | null | undefined;
  playRingSound: () => void;
  stopRingSound: () => void;
  incomingCallModalEnabled: boolean;
  /**
   * Ekran meşgul mü? (müşteri bağlamı açık VEYA herhangi bir modal açık).
   * Meşgulken yeni arama aktif OLMAZ — held olarak kuyruğa girer ve
   * onCallRequiresDecision tetiklenir (birleşik FIFO kuyruk).
   */
  isScreenBusy?: () => boolean;
  /** Meşgulken yeni arama geldiğinde ekranın karar modalını açması için */
  onCallRequiresDecision?: (call: QueuedCall) => void;
  /** Ekranda şu an işlem gören müşterinin telefonu (müşteri kartı / telefon filtresi). */
  getContextPhone?: () => string | null;
}

/**
 * Bazı Caller ID cihazları çalan bir aramanın numarasını ~15 sn'de bir yeniden
 * gönderir; arama ise son bildirimden 5 sn sonra "bitti" sayılır. Bu süre içinde
 * aynı numara tekrar gelirse YENİ arama değildir — hâlâ çalan aynı aramadır.
 */
const RING_RESEND_WINDOW_MS = 45000;

/** Telefonları karşılaştırmak için son 10 hane. */
const phoneKey = (p: string | null | undefined): string => (p || '').replace(/\D/g, '').slice(-10);

export interface UsePhoneCallsReturn {
  // SSE Bağlantı Durumu
  sseConnectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';

  // Gelen Arama (türetilmiş — geri uyumlu)
  incomingCall: PhoneCall | null;
  /** @deprecated Q6'da kaldırılacak — yerine updateActiveCallCustomerName kullan */
  setIncomingCall: React.Dispatch<React.SetStateAction<PhoneCall | null>>;
  isCallAnswered: boolean;
  setIsCallAnswered: (v: boolean) => void;
  activePhoneCallId: number | null;
  /** @deprecated Q6'da kaldırılacak — artık türetilmiş, doğrudan set edilemez */
  setActivePhoneCallId: React.Dispatch<React.SetStateAction<number | null>>;
  incomingCallPopupOpen: boolean;
  setIncomingCallPopupOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // YENİ — Kuyruk API
  activeCall: QueuedCall | null;
  pendingCalls: QueuedCall[];
  updateActiveCallCustomerName: (phone: string, name: string) => void;

  // Son Aramalar
  recentCalls: PhoneCall[];
  setRecentCalls: React.Dispatch<React.SetStateAction<PhoneCall[]>>;
  loadTodayCalls: () => Promise<void>;

  // API Fonksiyonları
  searchCustomerByPhone: (phoneNumber: string) => Promise<any>;
  savePhoneCallToDb: (phoneNumber: string, callerName?: string, accountId?: number) => Promise<number | null>;
  updatePhoneCall: (callId: number, isAnswered: boolean, isCallEnded?: boolean) => Promise<void>;

  // Arama Yönetimi — opsiyonel dbCallId (verilirse o pending/active arama hedeflenir)
  answerCall: (dbCallId?: number) => void;
  endCallUnanswered: (dbCallId?: number, phone?: string) => Promise<void>;
  endCallComplete: () => Promise<void>;
  /**
   * Bekleyen (held) aramayı "işlenen müşteri" olarak al: eski aktif arama
   * tamamlanır, hedef aktif+cevaplanmış olur ve müşterisi onCustomerFound ile
   * sol panele yüklenir. Karar modalındaki "Yeni Müşteriye Geç" bunu kullanır.
   * @returns müşteri kaydı bulunduysa true
   */
  takeCallAsCustomer: (phone: string) => Promise<boolean>;

  // Ref'ler (dışarıdan erişim için)
  callAnsweredTimeRef: React.MutableRefObject<Date | null>;
  isCallAnsweredRef: React.MutableRefObject<boolean>;
  activeCallPhoneRef: React.MutableRefObject<string | null>;

  // Callback'ler - component tarafından set edilecek
  onCustomerFound: (customer: PackageCustomer, defaultAddressId: number | null) => void;
  setOnCustomerFound: React.Dispatch<React.SetStateAction<(customer: PackageCustomer, defaultAddressId: number | null) => void>>;
  onNewCall: () => void;
  setOnNewCall: React.Dispatch<React.SetStateAction<() => void>>;
}

export const usePhoneCalls = (options: UsePhoneCallsOptions): UsePhoneCallsReturn => {
  const {
    companyId,
    screenId,
    branchId,
    packageScreenId,
    localServiceUrl,
    playRingSound,
    stopRingSound,
    incomingCallModalEnabled,
    isScreenBusy,
    onCallRequiresDecision,
    getContextPhone,
  } = options;

  // "Latest ref" pattern — SSE effect'in closure'ı bu callback'lerin en güncel
  // halini görsün diye her render'da güncellenir (effect yeniden abone olmaz).
  const isScreenBusyRef = useRef(isScreenBusy);
  isScreenBusyRef.current = isScreenBusy;
  const onCallRequiresDecisionRef = useRef(onCallRequiresDecision);
  onCallRequiresDecisionRef.current = onCallRequiresDecision;
  const getContextPhoneRef = useRef(getContextPhone);
  getContextPhoneRef.current = getContextPhone;
  // Zil bildirimi kesildiği için sonlandırılan aramalar: numara → bitiş zamanı + kayıt
  const ringEndedRecentlyRef = useRef<Map<string, { at: number; dbCallId: number | null; customerName?: string }>>(new Map());

  // ============================================================================
  // STATE — Dual: activeCall + pendingCalls[]
  // ============================================================================

  // SSE Bağlantı Durumu
  const [sseConnectionStatus, setSseConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const sseReconnectAttemptRef = useRef<number>(0);
  const sseReconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sseEventSourceRef = useRef<EventSource | null>(null);

  // Kuyruk State'leri
  const [activeCall, setActiveCallState] = useState<QueuedCall | null>(null);
  const [pendingCalls, setPendingCallsState] = useState<QueuedCall[]>([]);
  const [incomingCallPopupOpen, setIncomingCallPopupOpen] = useState(false);

  // Son Aramalar
  const [recentCalls, setRecentCalls] = useState<PhoneCall[]>([]);

  // Closure-safe ref'ler (handleIncomingCall içinde fresh state okumak için)
  const activeCallRef = useRef<QueuedCall | null>(null);
  const pendingCallsRef = useRef<QueuedCall[]>([]);
  // Per-phone yönetimi (her arama bağımsız)
  const timeoutsMapRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const lastCallTimesMapRef = useRef<Map<string, number>>(new Map());

  // Geri uyumluluk ref'leri (signal handler ve dış tüketiciler için)
  const activeCallPhoneRef = useRef<string | null>(null);
  const callAnsweredTimeRef = useRef<Date | null>(null);
  const isCallAnsweredRef = useRef<boolean>(false);
  // Eski API kalıntısı — artık per-phone Map'ten okunabilir ama signal handler kullanıyor
  const callEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Callback'ler - component tarafından set edilecek
  const [onCustomerFound, setOnCustomerFound] = useState<(customer: PackageCustomer, defaultAddressId: number | null) => void>(() => () => {});
  const [onNewCall, setOnNewCall] = useState<() => void>(() => () => {});

  // ============================================================================
  // ATOMIC STATE SETTERS — state + ref senkron tutulur
  // ============================================================================

  const setActiveCall = useCallback((updater: QueuedCall | null | ((prev: QueuedCall | null) => QueuedCall | null)) => {
    setActiveCallState(prev => {
      const next = typeof updater === 'function' ? (updater as (p: QueuedCall | null) => QueuedCall | null)(prev) : updater;
      activeCallRef.current = next;
      activeCallPhoneRef.current = next?.call.phone ?? null;
      isCallAnsweredRef.current = next?.isAnswered ?? false;
      callAnsweredTimeRef.current = next?.answeredAt ? new Date(next.answeredAt) : null;
      return next;
    });
  }, []);

  const setPendingCalls = useCallback((updater: QueuedCall[] | ((prev: QueuedCall[]) => QueuedCall[])) => {
    setPendingCallsState(prev => {
      const next = typeof updater === 'function' ? (updater as (p: QueuedCall[]) => QueuedCall[])(prev) : updater;
      pendingCallsRef.current = next;
      return next;
    });
  }, []);

  // ============================================================================
  // TÜRETİLMİŞ STATE — geri uyumlu API (incomingCall, activePhoneCallId, isCallAnswered)
  // ============================================================================

  const incomingCall = useMemo<PhoneCall | null>(() => {
    // YALNIZCA aktif arama. Önceden pendingCalls[0] fallback'i vardı; held
    // aramalar artık kalıcı olduğu için o fallback bekleyen müşteriyi sol
    // panele ve activeCustomerPhone hesabına sızdırırdı. Bekleyenler sağ üst
    // bildirim stack'i + karar modalı üzerinden yönetilir.
    return activeCall?.call ?? null;
  }, [activeCall]);

  const activePhoneCallId = useMemo<number | null>(() => {
    return activeCall?.dbCallId ?? null;
  }, [activeCall]);

  const isCallAnswered = useMemo<boolean>(() => {
    return activeCall?.isAnswered ?? false;
  }, [activeCall]);

  // ============================================================================
  // API FONKSİYONLARI
  // ============================================================================

  const searchCustomerByPhone = useCallback(async (phoneNumber: string) => {
    if (!companyId) return null;

    try {
      const { data: result } = await salesApi.get(`/api/PhoneCall/search-customer?phone=${encodeURIComponent(phoneNumber)}`);
      console.log('📞 [PhoneCall] Müşteri araması:', result);
      return result;
    } catch {
      // API kapalıysa sessizce devam et
    }
    return null;
  }, [companyId]);

  const savePhoneCallToDb = useCallback(async (phoneNumber: string, callerName?: string, accountId?: number): Promise<number | null> => {
    if (!companyId) return null;

    try {
      const { data: result } = await salesApi.post('/api/PhoneCall/create', {
        BranchId: branchId,
        PackageScreenId: packageScreenId || null,
        CallerPhone: phoneNumber,
        CallerName: callerName || null,
        CallType: 'incoming',
        IsAnswered: false,
        AccountId: accountId || null,
      });
      console.log('📞 [PhoneCall] Arama kaydedildi:', result);
      const callId = result.id || result.Id;
      console.log('📞 [PhoneCall] Arama ID:', callId);
      return callId;
    } catch (error) {
      console.error('📞 [PhoneCall] Arama kaydetme hatası:', error);
    }
    return null;
  }, [companyId, branchId, packageScreenId]);

  const updatePhoneCall = useCallback(async (callId: number, isAnswered: boolean, isCallEnded: boolean = false, callDurationOverride?: number | null) => {
    console.log('📞 [PhoneCall] updatePhoneCall çağrıldı:', { callId, isAnswered, isCallEnded });

    try {
      const now = new Date();
      const callEndTime = now.toISOString();

      // CallDuration hesapla
      let callDuration: number | null = callDurationOverride ?? null;
      if (callDuration === null && isCallEnded && isAnswered && callAnsweredTimeRef.current) {
        callDuration = Math.round((now.getTime() - callAnsweredTimeRef.current.getTime()) / 1000);
        console.log('📞 [PhoneCall] Çağrı süresi hesaplandı:', callDuration, 'saniye');
      }

      const requestBody = {
        CallEndTime: isCallEnded ? callEndTime : undefined,
        CallDuration: callDuration,
        IsAnswered: isAnswered,
      };

      console.log('📞 [PhoneCall] API isteği gönderiliyor:', requestBody);

      const { data: result } = await salesApi.post(`/api/PhoneCall/update/${callId}`, requestBody);
      console.log('📞 [PhoneCall] ✅ Arama güncellendi:', { callId, isAnswered, callDuration, isCallEnded, result });
    } catch (error) {
      console.error('📞 [PhoneCall] ❌ updatePhoneCall hatası:', error);
    }
  }, []);

  const loadTodayCalls = useCallback(async () => {
    if (!companyId) return;

    try {
      const { data: calls } = await salesApi.get(`/api/PhoneCall/today?packageScreenId=${packageScreenId || ''}`);
      console.log('📞 [PhoneCall] Bugünkü aramalar yüklendi:', (calls as any[])?.length ?? 0, calls);

      const formattedCalls: PhoneCall[] = (Array.isArray(calls) ? calls : []).map((c: any) => {
        const callId = c.id || c.Id;
        const callerPhone = c.callerPhone || c.CallerPhone;
        const callerName = c.callerName || c.CallerName;
        const callTime = c.callTime || c.CallTime;
        const customerName = c.customerName || c.CustomerName || callerName;
        const isAnsweredFlag = c.isAnswered ?? c.IsAnswered ?? false;
        const accountId = c.accountId || c.AccountId;

        let callTimeStr = '--:--';
        if (callTime) {
          const timeStr = typeof callTime === 'string' && !callTime.includes('Z') && !callTime.includes('+') ? callTime + 'Z' : callTime;
          const callDate = new Date(timeStr);
          if (!isNaN(callDate.getTime())) {
            callTimeStr = callDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' });
          }
        }

        return {
          id: callId,
          phone: callerPhone || 'Bilinmiyor',
          customerName: customerName,
          accountId: accountId,
          time: callTimeStr,
          isNew: !isAnsweredFlag,
        };
      });

      setRecentCalls(formattedCalls);
    } catch (error) {
      console.warn('📞 [PhoneCall] Son aramalar yüklenemedi (API kapalı olabilir)', error);
    }
  }, [companyId, packageScreenId]);

  // ============================================================================
  // MÜŞTERİ YÜKLEME — telefon numarasından müşteri + adres bilgisi
  // (handleIncomingCall, answerCall promotion ve takeCallAsCustomer ortak kullanır)
  // ============================================================================

  const loadCustomerForCall = useCallback(async (phoneNumber: string): Promise<{
    found: boolean;
    customer: PackageCustomer | null;
    defaultAddressId: number | null;
    customerName?: string;
    accountId?: number;
  }> => {
    const customerResult = await searchCustomerByPhone(phoneNumber);
    const found = customerResult?.found || customerResult?.Found;
    const customerName = customerResult?.customerName || customerResult?.CustomerName;
    const customerId = customerResult?.accountId || customerResult?.AccountId;

    if (!found) {
      return { found: false, customer: null, defaultAddressId: null, customerName, accountId: customerId };
    }

    const addresses = customerResult.addresses || customerResult.Addresses || [];
    const mappedAddresses: CustomerAddress[] = addresses.map((a: any) => {
      const addrId = a.id || a.Id;
      const addrLabel = a.addressLabel || a.AddressLabel || 'Adres';
      const district = a.district || a.District || '';
      const city = a.city || a.City || '';
      const address = a.address || a.Address || '';
      const isDefault = a.isDefault ?? a.IsDefault ?? false;

      return {
        id: addrId,
        label: addrLabel,
        address: `${district}, ${city} - ${address}`,
        isDefault: isDefault,
        city,
        district,
        mahalle: a.mahalle || a.Mahalle || '',
        cadde: a.cadde || a.Cadde || '',
        sokak: a.sokak || a.Sokak || '',
        apartman: a.apartman || a.Apartman || '',
        siteAdi: a.siteAdi || a.SiteAdi || '',
        blok: a.blok || a.Blok || '',
        binaNo: a.binaNo || a.BinaNo || '',
        kat: a.kat || a.Kat || '',
        daireNo: a.daireNo || a.DaireNo || '',
        deliveryNotes: a.deliveryNotes || a.DeliveryNotes || '',
        authorizedPerson: a.authorizedPerson || a.AuthorizedPerson || '',
        phone: a.phone || a.Phone || a.mobilePhone || a.MobilePhone || '',
      };
    });

    const customer: PackageCustomer = {
      id: customerId,
      name: customerName,
      phone: customerResult.phone || customerResult.Phone || customerResult.mobilePhone || customerResult.MobilePhone,
      addresses: mappedAddresses,
      totalOrders: customerResult.totalOrders || customerResult.TotalOrders || 0,
      lastOrderDate: customerResult.lastOrderDate || customerResult.LastOrderDate || '',
    };

    const defaultAddr = mappedAddresses.find((a: any) => a.isDefault);
    const defaultAddressId = defaultAddr?.id || (mappedAddresses.length > 0 ? mappedAddresses[0].id : null);

    return { found: true, customer, defaultAddressId, customerName, accountId: customerId };
  }, [searchCustomerByPhone]);

  // ============================================================================
  // QUEUE HELPERS — pending/active arama mutasyonları
  // ============================================================================

  /**
   * Telefona göre QueuedCall'u bul (önce active, sonra pending).
   * Eğer dbCallId verilirse onu kullan; yoksa phone match.
   */
  const findCallByPhone = useCallback((phone: string): { call: QueuedCall; isActive: boolean } | null => {
    if (activeCallRef.current?.call.phone === phone) {
      return { call: activeCallRef.current, isActive: true };
    }
    const pending = pendingCallsRef.current.find(p => p.call.phone === phone);
    if (pending) return { call: pending, isActive: false };
    return null;
  }, []);

  const findCallByDbId = useCallback((dbCallId: number): { call: QueuedCall; isActive: boolean } | null => {
    if (activeCallRef.current?.dbCallId === dbCallId) {
      return { call: activeCallRef.current, isActive: true };
    }
    const pending = pendingCallsRef.current.find(p => p.dbCallId === dbCallId);
    if (pending) return { call: pending, isActive: false };
    return null;
  }, []);

  /** Per-phone timeout'u temizler (clearTimeout + Map'ten sil) */
  const clearPhoneTimeout = useCallback((phone: string) => {
    const t = timeoutsMapRef.current.get(phone);
    if (t) {
      clearTimeout(t);
      timeoutsMapRef.current.delete(phone);
    }
  }, []);

  /**
   * Bir QueuedCall'un call alanını update et (örn. customerName eklemek için).
   * F1.3 cross-call name leak guard'ının yeni API'si.
   */
  const updateActiveCallCustomerName = useCallback((phone: string, name: string) => {
    const found = findCallByPhone(phone);
    if (!found) return;

    if (found.isActive) {
      setActiveCall(prev => prev ? { ...prev, call: { ...prev.call, customerName: name, isNew: false } } : null);
    } else {
      setPendingCalls(prev => prev.map(p =>
        p.call.phone === phone
          ? { ...p, call: { ...p.call, customerName: name, isNew: false } }
          : p
      ));
    }
  }, [findCallByPhone, setActiveCall, setPendingCalls]);

  // ============================================================================
  // ARAMA YÖNETİMİ — answerCall / endCallUnanswered / endCallComplete
  // ============================================================================

  /**
   * Aramayı cevapla. dbCallId verilirse o pending arama active'e yükseltilir
   * (eski active varsa otomatik endCallComplete edilir — Q3 davranışı).
   * Parametre yoksa mevcut activeCall (veya yoksa ilk pending) yanıtlanır.
   */
  const answerCall = useCallback((dbCallId?: number) => {
    let target: { call: QueuedCall; isActive: boolean } | null = null;

    if (dbCallId !== undefined) {
      target = findCallByDbId(dbCallId);
    } else {
      if (activeCallRef.current) {
        target = { call: activeCallRef.current, isActive: true };
      } else if (pendingCallsRef.current[0]) {
        target = { call: pendingCallsRef.current[0], isActive: false };
      }
    }

    if (!target) {
      console.warn('📞 [CallerID] ⚠️ answerCall hedef bulunamadı, dbCallId:', dbCallId);
      return;
    }

    console.log('📞 [CallerID] ✅ Arama cevaplandı:', target.call.call.phone, '| dbCallId:', target.call.dbCallId, '| isActive:', target.isActive);

    // Hedef pending ise: eski active'i otomatik bitir, hedef'i promote et
    if (!target.isActive) {
      const prevActive = activeCallRef.current;
      if (prevActive) {
        console.log('📞 [CallerID] 🔄 Eski aktif arama otomatik tamamlandı:', prevActive.call.phone);
        // Eski active'i DB'de "yanıtlanmış + bitti" olarak kapat (konuşma bitti varsayımı)
        const wasAnswered = prevActive.isAnswered;
        let callDuration: number | null = null;
        if (wasAnswered && prevActive.answeredAt) {
          callDuration = Math.round((Date.now() - prevActive.answeredAt) / 1000);
        }
        clearPhoneTimeout(prevActive.call.phone);
        lastCallTimesMapRef.current.delete(prevActive.call.phone);
        if (prevActive.dbCallId && !prevActive.dbClosed) {
          updatePhoneCall(prevActive.dbCallId, wasAnswered, true, callDuration).then(() => {
            setTimeout(() => loadTodayCalls(), 500);
          });
        }
      }

      // Pending'den çıkar, active'e yükselt
      setPendingCalls(prev => prev.filter(p => p.call.phone !== target!.call.call.phone));
      const promoted: QueuedCall = {
        ...target.call,
        isAnswered: true,
        answeredAt: Date.now(),
        call: { ...target.call.call, isNew: false },
      };
      setActiveCall(promoted);
      stopRingSound();
      setIncomingCallPopupOpen(false);
      clearPhoneTimeout(promoted.call.phone);
      if (promoted.dbCallId && !promoted.dbClosed) {
        updatePhoneCall(promoted.dbCallId, true, false);
      }

      // Bug fix: pending'den yükseltilen aramanın müşterisi sol panele hiç
      // yüklenmiyordu (arama anındaki onCustomerFound cross-call guard'ı
      // pending için tetiklenmez). Promotion sonrası müşteriyi yeniden ara.
      loadCustomerForCall(promoted.call.phone).then(result => {
        if (result.found && result.customer && activeCallRef.current?.call.phone === promoted.call.phone) {
          onCustomerFound(result.customer, result.defaultAddressId);
          setActiveCall(prev => prev ? {
            ...prev,
            call: { ...prev.call, customerName: result.customerName || prev.call.customerName, isNew: false },
          } : null);
        }
      }).catch(() => {});
    } else {
      // Hedef zaten active — basit cevaplama
      stopRingSound();
      setIncomingCallPopupOpen(false);
      clearPhoneTimeout(target.call.call.phone);
      setActiveCall(prev => prev ? {
        ...prev,
        isAnswered: true,
        answeredAt: Date.now(),
        call: { ...prev.call, isNew: false },
      } : null);
      if (target.call.dbCallId) {
        updatePhoneCall(target.call.dbCallId, true, false);
      } else {
        console.warn('📞 [CallerID] ⚠️ dbCallId null, DB güncellenemedi');
      }
    }
  }, [findCallByDbId, clearPhoneTimeout, setActiveCall, setPendingCalls, stopRingSound, updatePhoneCall, loadTodayCalls, loadCustomerForCall, onCustomerFound]);

  /**
   * Aramayı cevapsız sonlandır. dbCallId verilirse o arama hedeflenir
   * (pending veya active); bulunamazsa/yoksa phone ile aranır (held aramalarda
   * DB kaydı henüz oluşmamış olabilir). İkisi de yoksa mevcut activeCall bitirilir.
   */
  const endCallUnanswered = useCallback(async (dbCallId?: number, phone?: string) => {
    let target: { call: QueuedCall; isActive: boolean } | null = null;

    if (dbCallId !== undefined) {
      target = findCallByDbId(dbCallId);
    }
    if (!target && phone) {
      target = findCallByPhone(phone);
    }
    if (!target && dbCallId === undefined && !phone && activeCallRef.current) {
      target = { call: activeCallRef.current, isActive: true };
    }

    if (!target) {
      console.warn('📞 [CallerID] ⚠️ endCallUnanswered hedef bulunamadı, dbCallId:', dbCallId, 'phone:', phone);
      return;
    }

    const wasAnswered = target.call.isAnswered;
    console.log('📞 [CallerID] Arama sonlandı:', wasAnswered ? '(CEVAPLANMIŞ)' : '(CEVAPSIZ)', '| phone:', target.call.call.phone, '| isActive:', target.isActive);

    clearPhoneTimeout(target.call.call.phone);
    lastCallTimesMapRef.current.delete(target.call.call.phone);

    let callDuration: number | null = null;
    if (wasAnswered && target.call.answeredAt) {
      callDuration = Math.round((Date.now() - target.call.answeredAt) / 1000);
    }

    if (target.call.dbCallId && !target.call.dbClosed) {
      await updatePhoneCall(target.call.dbCallId, wasAnswered, true, callDuration);
    }

    if (target.isActive) {
      setActiveCall(null);
      stopRingSound();
      setIncomingCallPopupOpen(false);
    } else {
      setPendingCalls(prev => prev.filter(p => p.call.phone !== target!.call.call.phone));
    }

    setTimeout(() => {
      loadTodayCalls();
    }, 500);
  }, [findCallByDbId, findCallByPhone, clearPhoneTimeout, setActiveCall, setPendingCalls, stopRingSound, updatePhoneCall, loadTodayCalls]);

  /**
   * Aktif aramayı tamamlanmış (yanıtlanmış + bitti) olarak sonlandır.
   * Pending'lere dokunulmaz — kullanıcı kuyruktan sıradakini seçer.
   */
  const endCallComplete = useCallback(async () => {
    const ac = activeCallRef.current;
    if (!ac) {
      console.log('📞 [CallerID] endCallComplete çağrıldı ama active yok');
      return;
    }

    console.log('📞 [CallerID] Arama tamamlandı:', ac.call.phone);

    clearPhoneTimeout(ac.call.phone);
    lastCallTimesMapRef.current.delete(ac.call.phone);

    let callDuration: number | null = null;
    if (ac.answeredAt) {
      callDuration = Math.round((Date.now() - ac.answeredAt) / 1000);
    }

    if (ac.dbCallId && !ac.dbClosed) {
      await updatePhoneCall(ac.dbCallId, true, true, callDuration);
    }

    setActiveCall(null);
    stopRingSound();
    setIncomingCallPopupOpen(false);

    setTimeout(() => {
      loadTodayCalls();
    }, 500);
  }, [clearPhoneTimeout, setActiveCall, stopRingSound, updatePhoneCall, loadTodayCalls]);

  /**
   * Bekleyen (held) veya aktif bir aramayı "işlenen müşteri" olarak al.
   * Karar modalındaki "Yeni Müşteriye Geç" bu fonksiyonu kullanır:
   *  1) Eski aktif arama varsa tamamlandı olarak kapatılır
   *  2) Hedef arama pending'den çıkarılıp aktif + cevaplanmış yapılır
   *  3) Müşteri yeniden aranır ve onCustomerFound ile sol panele yüklenir
   */
  const takeCallAsCustomer = useCallback(async (phone: string): Promise<boolean> => {
    const found = findCallByPhone(phone);
    if (!found) {
      console.warn('📞 [CallerID] ⚠️ takeCallAsCustomer hedef bulunamadı:', phone);
      return false;
    }

    // 1) Eski aktif aramayı tamamla (hedef farklı bir aramaysa)
    const prevActive = activeCallRef.current;
    if (prevActive && prevActive.call.phone !== phone) {
      console.log('📞 [CallerID] 🔄 Müşteri geçişi — eski aktif arama kapatılıyor:', prevActive.call.phone);
      clearPhoneTimeout(prevActive.call.phone);
      lastCallTimesMapRef.current.delete(prevActive.call.phone);
      if (prevActive.dbCallId && !prevActive.dbClosed) {
        let dur: number | null = null;
        if (prevActive.isAnswered && prevActive.answeredAt) {
          dur = Math.round((Date.now() - prevActive.answeredAt) / 1000);
        }
        updatePhoneCall(prevActive.dbCallId, prevActive.isAnswered, true, dur).then(() => {
          setTimeout(() => loadTodayCalls(), 500);
        });
      }
    }

    // 2) Pending'den çıkar, aktif + cevaplanmış yap
    if (!found.isActive) {
      setPendingCalls(prev => prev.filter(p => p.call.phone !== phone));
    }
    clearPhoneTimeout(phone);

    const promoted: QueuedCall = {
      ...found.call,
      isAnswered: true,
      answeredAt: found.call.answeredAt ?? Date.now(),
      call: { ...found.call.call, isNew: false },
    };
    setActiveCall(promoted);
    stopRingSound();
    setIncomingCallPopupOpen(false);

    // DB kaydı hâlâ açıksa cevaplanmış işaretle
    if (promoted.dbCallId && !promoted.dbClosed) {
      updatePhoneCall(promoted.dbCallId, true, false);
    }

    console.log('📞 [CallerID] ✅ Bekleyen müşteri işleme alındı:', phone);

    // 3) Müşteriyi yükle ve sol paneli doldur
    try {
      const result = await loadCustomerForCall(phone);
      if (result.found && result.customer) {
        if (activeCallRef.current?.call.phone === phone) {
          onCustomerFound(result.customer, result.defaultAddressId);
          setActiveCall(prev => prev ? {
            ...prev,
            call: { ...prev.call, customerName: result.customerName || prev.call.customerName, isNew: false },
          } : null);
        }
        return true;
      }
    } catch (err) {
      console.error('📞 [CallerID] takeCallAsCustomer müşteri yükleme hatası:', err);
    }
    return false;
  }, [findCallByPhone, clearPhoneTimeout, setActiveCall, setPendingCalls, stopRingSound, updatePhoneCall, loadTodayCalls, loadCustomerForCall, onCustomerFound]);

  // ============================================================================
  // LEGACY API SHIMS — eski tüketicilerin Q6'ya kadar çalışması için
  // ============================================================================

  /** @deprecated Q6'da kaldırılacak. */
  const setIncomingCall = useCallback<React.Dispatch<React.SetStateAction<PhoneCall | null>>>((updater) => {
    if (typeof updater === 'function') {
      // setIncomingCall(prev => ...) çağrıları — eskiden activeCall.call'u update ediyordu.
      // F1.3 cross-call name leak guard'ı bunu kullanıyor — şimdi updateActiveCallCustomerName var.
      const upd = updater as (p: PhoneCall | null) => PhoneCall | null;
      const ac = activeCallRef.current;
      if (!ac) return;
      const nextCall = upd(ac.call);
      if (!nextCall) {
        // Eski "(prev) => null" kullanımı — activeCall'ı bitir
        setActiveCall(null);
      } else {
        setActiveCall(prev => prev ? { ...prev, call: nextCall } : null);
      }
    } else if (updater === null) {
      setActiveCall(null);
    } else {
      console.warn('📞 [CallerID] setIncomingCall(directObject) deprecated — kuyruk API kullanın');
    }
  }, [setActiveCall]);

  /** @deprecated Q6'da kaldırılacak. */
  const setActivePhoneCallId = useCallback<React.Dispatch<React.SetStateAction<number | null>>>((updater) => {
    const next = typeof updater === 'function'
      ? (updater as (p: number | null) => number | null)(activeCallRef.current?.dbCallId ?? null)
      : updater;
    if (next === null && activeCallRef.current) {
      setActiveCall(prev => prev ? { ...prev, dbCallId: null } : null);
    } else if (next !== null && activeCallRef.current) {
      setActiveCall(prev => prev ? { ...prev, dbCallId: next } : null);
    }
  }, [setActiveCall]);

  /** @deprecated Q6'da kullanılmıyor — activeCall.isAnswered'ı update eder. */
  const setIsCallAnswered = useCallback((v: boolean) => {
    setActiveCall(prev => prev ? { ...prev, isAnswered: v, answeredAt: v ? (prev.answeredAt ?? Date.now()) : null } : null);
  }, [setActiveCall]);

  // ============================================================================
  // SSE / ELECTRON ENTEGRASYONU — Yeni arama event handler'ı
  // ============================================================================

  useEffect(() => {
    const isElectron = !!(window as any).electronAPI;
    let eventSource: EventSource | null = null;
    let isCleanedUp = false;

    const CALL_END_TIMEOUT_MS = 5000;
    const DUPLICATE_EVENT_THRESHOLD_MS = 2000;

    /**
     * Aramayı sonlandır (timeout veya başka sebeple).
     * Hem active hem pending için çalışır.
     */
    const handleCallTimeout = async (phone: string) => {
      const found = findCallByPhone(phone);
      if (!found) return;

      const wasAnswered = found.call.isAnswered;
      let callDuration: number | null = null;
      if (wasAnswered && found.call.answeredAt) {
        callDuration = Math.round((Date.now() - found.call.answeredAt) / 1000);
      }

      timeoutsMapRef.current.delete(phone);
      lastCallTimesMapRef.current.delete(phone);

      // HELD PENDING: kuyruktan SİLİNMEZ (birleşik kuyruk kuralı) — zil bitti
      // olarak işaretlenir, DB kaydı kapatılır ve bildirim sağ üstte kasiyer
      // karar verene / sırası gelene kadar beklemeye devam eder.
      if (!found.isActive && found.call.isHeld) {
        console.log('📞 [CallerID] 5sn timeout — held arama kuyrukta BEKLEMEDE kalıyor:', phone);
        if (found.call.dbCallId && !found.call.dbClosed) {
          await updatePhoneCall(found.call.dbCallId, wasAnswered, true, callDuration);
        }
        setPendingCalls(prev => prev.map(p =>
          p.call.phone === phone ? { ...p, ringEnded: true, dbClosed: true } : p
        ));
        setTimeout(() => loadTodayCalls(), 500);
        return;
      }

      console.log('📞 [CallerID] 5sn timeout — arama sonlandı:', phone, '| isActive:', found.isActive);

      if (found.call.dbCallId && !found.call.dbClosed) {
        await updatePhoneCall(found.call.dbCallId, wasAnswered, true, callDuration);
      }

      if (found.isActive) {
        ringEndedRecentlyRef.current.set(phone, {
          at: Date.now(),
          dbCallId: found.call.dbCallId,
          customerName: found.call.call.customerName,
        });
        setActiveCall(null);
        stopRingSound();
        setIncomingCallPopupOpen(false);
      } else {
        setPendingCalls(prev => prev.filter(p => p.call.phone !== phone));
      }

      setTimeout(() => loadTodayCalls(), 500);
    };

    /**
     * Yeni arama event handler — KUYRUK MİMARİSİ:
     * - activeCall yoksa: yeni aramayı active yap, ses çal, popup aç
     * - activeCall varsa: pending kuyruğa ekle, ses ÇALMAZ (Q4)
     * - Duplicate event: timeout reset, başka iş yok
     */
    const handleIncomingCall = async (data: {
      deviceSerial?: string;
      line?: string;
      phoneNumber: string;
      dateTime?: string;
      other?: string;
      receivedAt?: string;
    }) => {
      if (isCleanedUp) {
        console.log('📞 [CallerID] Effect cleanup oldu, event ignore ediliyor');
        return;
      }

      console.log('📞 [CallerID] Gelen arama alındı:', data);

      const phoneNumber = data.phoneNumber?.trim() || 'Bilinmiyor';
      const currentTime = new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
      const now = Date.now();

      // 1) Duplicate filter: aynı numara DUPLICATE_EVENT_THRESHOLD_MS içinde tekrar geldi
      const lastTime = lastCallTimesMapRef.current.get(phoneNumber) || 0;
      const timeSinceLast = now - lastTime;
      if (timeSinceLast < DUPLICATE_EVENT_THRESHOLD_MS) {
        // Bu aynı arama — sadece timeout reset
        const found = findCallByPhone(phoneNumber);
        if (found && !found.call.isAnswered) {
          const existing = timeoutsMapRef.current.get(phoneNumber);
          if (existing) clearTimeout(existing);
          const newT = setTimeout(() => handleCallTimeout(phoneNumber), CALL_END_TIMEOUT_MS);
          timeoutsMapRef.current.set(phoneNumber, newT);
        }
        return;
      }
      lastCallTimesMapRef.current.set(phoneNumber, now);

      // 2) Aynı numara zaten active veya pending'de mi? (uzun süreli aynı arama)
      const existingCall = findCallByPhone(phoneNumber);
      if (existingCall) {
        console.log('📞 [CallerID] Aynı numara zaten kuyrukta, timeout reset:', phoneNumber);
        if (!existingCall.call.isAnswered) {
          const existing = timeoutsMapRef.current.get(phoneNumber);
          if (existing) clearTimeout(existing);
          const newT = setTimeout(() => handleCallTimeout(phoneNumber), CALL_END_TIMEOUT_MS);
          timeoutsMapRef.current.set(phoneNumber, newT);
        }
        return;
      }

      // 2b) Hâlâ çalan aynı aramanın yeniden bildirimi mi?
      //  - Ekranda zaten bu müşteriyle çalışılıyorsa (sipariş giriliyor olabilir)
      //    hiçbir şey yapılmaz: karar penceresi açılmaz, yeni kayıt oluşmaz.
      //  - Ekran boşsa arama yeni kayıt açmadan aynı kayıtla geri getirilir.
      // Önceden bu durum her 15 sn'de "YENİ GELEN ARAMA" penceresi açıyordu;
      // kasiyer aynı müşteri sanıp "Yeni Müşteriye Geç"e basınca yarım sipariş kapanıyordu.
      for (const [p, info] of Array.from(ringEndedRecentlyRef.current.entries())) {
        if (now - info.at > RING_RESEND_WINDOW_MS) ringEndedRecentlyRef.current.delete(p);
      }
      const contextPhone = getContextPhoneRef.current ? getContextPhoneRef.current() : null;
      const isContextCaller = !!contextPhone && phoneKey(contextPhone) === phoneKey(phoneNumber);
      const resent = ringEndedRecentlyRef.current.get(phoneNumber);
      if (isContextCaller && resent) {
        console.log('📞 [CallerID] Ekrandaki müşterinin araması tekrar bildirildi — yok sayıldı:', phoneNumber);
        resent.at = now; // çalmaya devam ettiği sürece pencere uzar
        return;
      }
      if (resent) {
        const busyNow = !!(isScreenBusyRef.current && isScreenBusyRef.current());
        if (activeCallRef.current === null && !busyNow) {
          console.log('📞 [CallerID] Aynı arama hâlâ çalıyor — mevcut kayıtla geri getirildi:', phoneNumber);
          ringEndedRecentlyRef.current.delete(phoneNumber);
          setActiveCall({
            call: { id: Date.now(), phone: phoneNumber, customerName: resent.customerName, time: currentTime, isNew: !resent.customerName },
            dbCallId: resent.dbCallId,
            startedAt: now,
            answeredAt: null,
            isAnswered: false,
            isHeld: false,
            ringEnded: false,
            dbClosed: true,
          });
          if (incomingCallModalEnabled) setIncomingCallPopupOpen(true);
          const t = setTimeout(() => handleCallTimeout(phoneNumber), CALL_END_TIMEOUT_MS);
          timeoutsMapRef.current.set(phoneNumber, t);
          callEndTimeoutRef.current = t;
          const info = await loadCustomerForCall(phoneNumber);
          if (info.found && info.customer && (activeCallRef.current as QueuedCall | null)?.call.phone === phoneNumber) {
            onCustomerFound(info.customer, info.defaultAddressId);
          }
          return;
        }
        // Ekran başka bir müşteriyle meşgul: normal akış (bekletilir, karar sorulur)
        ringEndedRecentlyRef.current.delete(phoneNumber);
      }

      // 3) Yeni arama — QueuedCall objesi oluştur
      const newCall: PhoneCall = {
        id: Date.now(),
        phone: phoneNumber,
        customerName: undefined,
        time: currentTime,
        isNew: true,
      };

      // BİRLEŞİK KUYRUK ROUTING: yalnızca "activeCall yok" yetmez — ekranda
      // önceki müşterinin bağlamı (currentCustomer / filtre) veya açık bir
      // modal varsa da arama held olarak kuyruğa girer (kasiyer karar verir).
      const screenBusy = !!(isScreenBusyRef.current && isScreenBusyRef.current());
      const goesActive = activeCallRef.current === null && !screenBusy;

      const queuedCall: QueuedCall = {
        call: newCall,
        dbCallId: null,
        startedAt: now,
        answeredAt: null,
        isAnswered: false,
        isHeld: !goesActive,
        ringEnded: false,
        dbClosed: false,
      };

      // 4) ROUTING — ekran boşsa aktif; meşgulse held-pending + karar modalı
      if (goesActive) {
        console.log('📞 [CallerID] YENİ ARAMA başladı (aktif):', phoneNumber);
        setActiveCall(queuedCall);

        // Q4: Ring sound SADECE ilk arama için (activeCall yoktu)
        playRingSound();

        if (incomingCallModalEnabled) {
          setIncomingCallPopupOpen(true);
        }

        // onNewCall callback — F1.5 guard tarafından korunuyor (anyModalOpen)
        onNewCall();
      } else {
        console.log('📞 [CallerID] EKRAN MEŞGUL — arama held olarak kuyruğa alındı:', phoneNumber,
          '| aktif:', activeCallRef.current?.call.phone ?? '(yok)', '| screenBusy:', screenBusy);
        setPendingCalls(prev => [...prev, queuedCall]);
        // Q4: Held aramalarda ses çalmaz — kasiyer meşgul, zorunlu karar
        // modalı + sağ üst bildirim uyarır. onNewCall ÇAĞRILMAZ: önceki
        // müşterinin bağlamı korunur (kullanıcı kuralı).
        onCallRequiresDecisionRef.current?.(queuedCall);
      }

      // 5) Per-phone 5sn timeout
      const t = setTimeout(() => handleCallTimeout(phoneNumber), CALL_END_TIMEOUT_MS);
      timeoutsMapRef.current.set(phoneNumber, t);

      // 6) Geri uyumluluk: legacy callEndTimeoutRef'i sadece active için tut
      // (signal handler bunu kullanıyor)
      if (goesActive) {
        callEndTimeoutRef.current = t;
      }

      // 7) Müşteri ara + DB kayıt (ortak helper: loadCustomerForCall)
      const customerInfo = await loadCustomerForCall(phoneNumber);
      console.log('📞 [PhoneCall] Müşteri sonucu detay:', {
        found: customerInfo.found,
        addressCount: customerInfo.customer?.addresses?.length ?? 0,
      });

      const callId = await savePhoneCallToDb(phoneNumber, customerInfo.customerName, customerInfo.accountId);

      // queuedCall'a dbCallId ve customerName ekle (active veya pending'de neredeyse)
      {
        const currentlyActive = activeCallRef.current?.call.phone === phoneNumber;
        if (currentlyActive) {
          setActiveCall(prev => prev ? {
            ...prev,
            dbCallId: callId ?? prev.dbCallId,
            call: { ...prev.call, customerName: customerInfo.customerName || prev.call.customerName },
          } : null);
        } else {
          setPendingCalls(prev => prev.map(p =>
            p.call.phone === phoneNumber
              ? { ...p, dbCallId: callId ?? p.dbCallId, call: { ...p.call, customerName: customerInfo.customerName || p.call.customerName } }
              : p
          ));
        }
        if (callId !== null && callId !== undefined) {
          console.log('📞 [CallerID] DB kaydedildi, ID:', callId, 'Müşteri:', customerInfo.customerName || 'Bilinmiyor');
        }
      }

      if (customerInfo.found && customerInfo.customer) {
        // F1.3 / Cross-call guard: onCustomerFound callback SADECE bu arama
        // hâlâ activeCall ise tetiklenir. Held/pending aramada müşteri
        // bulunsa bile arka plan formuna yansımamalı.
        if (activeCallRef.current?.call.phone === phoneNumber) {
          onCustomerFound(customerInfo.customer, customerInfo.defaultAddressId);

          // isNew = false (customer bulundu)
          setActiveCall(prev => prev ? {
            ...prev,
            call: { ...prev.call, customerName: customerInfo.customerName, isNew: false },
          } : null);
        }

        console.log('✅ [Customer] Müşteri yüklendi:', {
          name: customerInfo.customerName,
          addressCount: customerInfo.customer.addresses?.length ?? 0,
        });
      }
    };

    // Electron'da IPC EK bir kanal: local-service olayı hem IPC hem SSE ile
    // yayınlıyor. Eskiden burada "Electron ise sadece IPC" deniyordu; ekran
    // local-service içindeki bir sekme view'ında çalıştığı ve IPC yalnızca ana
    // pencereye gittiği için hiçbir arama gelmiyordu. SSE her iki ortamda da
    // kurulur; mükerrer olaylar handleIncomingCall'daki
    // DUPLICATE_EVENT_THRESHOLD_MS filtresine takılır.
    if (isElectron) {
      console.log('📞 [CallerID] Electron ortamı tespit edildi, IPC dinleyicileri ekleniyor...');

      const electronAPI = (window as any).electronAPI;

      if (electronAPI.onCallerIdIncoming) {
        electronAPI.onCallerIdIncoming(handleIncomingCall);
      }

      if (electronAPI.onCallerIdSignal) {
        electronAPI.onCallerIdSignal((data: {
          deviceModel: string;
          deviceSerial: string;
          signal1: number;
          signal2: number;
          isConnected: boolean;
        }) => {
          // Gerekirse cihaz durumunu göstermek için kullanılabilir
        });
      }
    }

    {
      // SSE — hem tarayıcı hem Electron. Sağ üstteki Caller ID bağlantı
      // göstergesi de bu bağlantının durumunu yansıtır.
      console.log('📞 [CallerID] SSE bağlantısı kuruluyor...', isElectron ? '(Electron + IPC)' : '(tarayıcı)');

      const sseBaseUrl = localServiceUrl || 'http://localhost:3003';
      const sseUrl = `${sseBaseUrl}/api/caller-id/events`;

      const MAX_RECONNECT_ATTEMPTS = 50;
      const INITIAL_RECONNECT_DELAY = 1000;
      const MAX_RECONNECT_DELAY = 30000;

      const connectSSE = () => {
        if (isCleanedUp) {
          console.log('📞 [CallerID SSE] Cleanup oldu, bağlantı kurulamıyor');
          return;
        }

        console.log('📞 [CallerID SSE] Bağlantı kuruluyor:', sseUrl, '| Deneme:', sseReconnectAttemptRef.current + 1);
        setSseConnectionStatus('connecting');

        try {
          if (sseEventSourceRef.current) {
            sseEventSourceRef.current.close();
          }

          const newEventSource = new EventSource(sseUrl);
          sseEventSourceRef.current = newEventSource;
          eventSource = newEventSource;

          newEventSource.onopen = () => {
            console.log('📞 [CallerID SSE] ✅ Bağlantı kuruldu!');
            setSseConnectionStatus('connected');
            sseReconnectAttemptRef.current = 0;
          };

          newEventSource.addEventListener('connected', (event) => {
            try {
              const eventData = JSON.parse(event.data);
              console.log('📞 [CallerID SSE] Bağlantı onaylandı:', eventData);
              setSseConnectionStatus('connected');
            } catch (e) {
              console.log('📞 [CallerID SSE] Bağlantı onaylandı');
            }
          });

          newEventSource.addEventListener('incoming-call', (event) => {
            try {
              const eventData = JSON.parse(event.data);
              console.log('📞 [CallerID SSE] 📞 Gelen arama:', eventData);
              handleIncomingCall(eventData.data || eventData);
            } catch (e) {
              console.error('📞 [CallerID SSE] Arama verisi parse hatası:', e);
            }
          });

          newEventSource.addEventListener('signal', (event) => {
            if (sseConnectionStatus !== 'connected') {
              setSseConnectionStatus('connected');
            }

            try {
              const signalData = JSON.parse(event.data);
              const signalDataInner = signalData.data || signalData;
              const signal1 = signalDataInner.signal1 || 0;
              const signal2 = signalDataInner.signal2 || 0;

              // Signal yalnızca activeCall'ın timeout'unu sıfırlar.
              // Pending aramalar kendi timeout'larıyla yaşar.
              const ac = activeCallRef.current;
              if (ac && !ac.isAnswered && (signal1 > 15 || signal2 > 15)) {
                const existing = timeoutsMapRef.current.get(ac.call.phone);
                if (existing) {
                  clearTimeout(existing);
                  const newT = setTimeout(() => handleCallTimeout(ac.call.phone), CALL_END_TIMEOUT_MS);
                  timeoutsMapRef.current.set(ac.call.phone, newT);
                  callEndTimeoutRef.current = newT;
                }
              }
            } catch (e) {
              // Parse hatası - yoksay
            }
          });

          newEventSource.onerror = (error) => {
            console.warn('📞 [CallerID SSE] ❌ Bağlantı hatası!', error);
            setSseConnectionStatus('error');

            if (isCleanedUp) {
              return;
            }

            newEventSource.close();
            sseEventSourceRef.current = null;

            if (sseReconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
              console.error('📞 [CallerID SSE] ⛔ Maksimum yeniden bağlanma denemesi aşıldı!');
              setSseConnectionStatus('disconnected');
              return;
            }

            sseReconnectAttemptRef.current++;
            const delay = Math.min(
              INITIAL_RECONNECT_DELAY * Math.pow(1.5, sseReconnectAttemptRef.current - 1),
              MAX_RECONNECT_DELAY
            );

            console.log(`📞 [CallerID SSE] 🔄 ${delay / 1000}sn sonra yeniden bağlanılacak... (${sseReconnectAttemptRef.current}/${MAX_RECONNECT_ATTEMPTS})`);

            if (sseReconnectTimeoutRef.current) {
              clearTimeout(sseReconnectTimeoutRef.current);
            }

            sseReconnectTimeoutRef.current = setTimeout(() => {
              if (!isCleanedUp) {
                connectSSE();
              }
            }, delay);
          };

        } catch (error) {
          console.error('📞 [CallerID SSE] Bağlantı kurulamadı:', error);
          setSseConnectionStatus('error');

          if (!isCleanedUp && sseReconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
            sseReconnectAttemptRef.current++;
            const delay = Math.min(
              INITIAL_RECONNECT_DELAY * Math.pow(1.5, sseReconnectAttemptRef.current - 1),
              MAX_RECONNECT_DELAY
            );

            sseReconnectTimeoutRef.current = setTimeout(() => {
              if (!isCleanedUp) {
                connectSSE();
              }
            }, delay);
          }
        }
      };

      connectSSE();
    }

    // Cleanup
    return () => {
      console.log('📞 [CallerID] Dinleyiciler kaldırılıyor...');
      isCleanedUp = true;

      // Tüm per-phone timeout'ları temizle
      timeoutsMapRef.current.forEach((t) => clearTimeout(t));
      timeoutsMapRef.current.clear();
      lastCallTimesMapRef.current.clear();

      if (callEndTimeoutRef.current) {
        clearTimeout(callEndTimeoutRef.current);
        callEndTimeoutRef.current = null;
      }

      if (sseReconnectTimeoutRef.current) {
        clearTimeout(sseReconnectTimeoutRef.current);
        sseReconnectTimeoutRef.current = null;
      }

      if (isElectron) {
        const electronAPI = (window as any).electronAPI;
        if (electronAPI?.removeCallerIdListeners) {
          electronAPI.removeCallerIdListeners();
        }
      }

      if (sseEventSourceRef.current) {
        sseEventSourceRef.current.close();
        sseEventSourceRef.current = null;
        setSseConnectionStatus('disconnected');
        console.log('📞 [CallerID SSE] Bağlantı kapatıldı.');
      } else if (eventSource) {
        eventSource.close();
        console.log('📞 [CallerID SSE] Bağlantı kapatıldı.');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, screenId, branchId, localServiceUrl, incomingCallModalEnabled]);

  // Son aramaları yükle - screenData yüklendikten sonra
  useEffect(() => {
    if (companyId && packageScreenId) {
      loadTodayCalls();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, packageScreenId]);

  return {
    // SSE Bağlantı Durumu
    sseConnectionStatus,

    // Gelen Arama (türetilmiş, geri uyumlu)
    incomingCall,
    setIncomingCall,
    isCallAnswered,
    setIsCallAnswered,
    activePhoneCallId,
    setActivePhoneCallId,
    incomingCallPopupOpen,
    setIncomingCallPopupOpen,

    // YENİ — Kuyruk API
    activeCall,
    pendingCalls,
    updateActiveCallCustomerName,

    // Son Aramalar
    recentCalls,
    setRecentCalls,
    loadTodayCalls,

    // API Fonksiyonları
    searchCustomerByPhone,
    savePhoneCallToDb,
    updatePhoneCall,

    // Arama Yönetimi
    answerCall,
    endCallUnanswered,
    endCallComplete,
    takeCallAsCustomer,

    // Ref'ler
    callAnsweredTimeRef,
    isCallAnsweredRef,
    activeCallPhoneRef,

    // Callback'ler
    onCustomerFound,
    setOnCustomerFound,
    onNewCall,
    setOnNewCall,
  };
};
